/**
 * @file Mixin that handles connections in canvas
 */

import type { BrowserJsPlumbInstance } from '@jsplumb/browser-ui'
import type { OverlaySpec } from '@jsplumb/common'
import type { BezierConnector } from '@jsplumb/connector-bezier'
import type { Connection as JsPlumbConnection } from '@jsplumb/core'
import { getJsPlumbBezierConnector, initiateJsPlumb } from 'bits/jsplumb'
import { Component, Getter, namespace, Vue, Watch } from 'bits/vue'
import difference from 'lodash.difference'
import type { Id, PostConnection } from 'types'

interface TrackedConnection {
  postConnection: PostConnection
  jsplumbConnection: JsPlumbConnection<HTMLElement>
}

@Component({})
export default class CanvasConnectionMixin extends Vue {
  jsPlumb?: BrowserJsPlumbInstance
  BezierConnector?: BezierConnector
  @Getter isCanvas!: boolean
  @namespace('postConnection').Getter readonly currentConnections!: PostConnection[]
  @namespace('postConnection').State readonly postToDisconnectFromId?: Id
  @namespace('postConnection').Action completeDisconnection!: ({ connectionId }) => void

  existingConnections: Record<Id, TrackedConnection | undefined> = {}

  async mounted(): Promise<void> {
    if (!this.isCanvas) return
    const [jsPlumb, BezierConnector] = await Promise.all([initiateJsPlumb(this.$el), getJsPlumbBezierConnector()])
    if (jsPlumb == null || BezierConnector == null) return
    this.jsPlumb = jsPlumb
    this.BezierConnector = BezierConnector
    this.drawAllConnections()
  }

  @Watch('currentConnections')
  handleConnectionChanged(newVal, oldVal): void {
    this.jsPlumb?.batch(() => {
      const removedConnections: PostConnection[] = difference(oldVal, newVal) ?? []
      removedConnections.forEach(this.removeConnection)
    })
    this.$nextTick(() => {
      // wait for DOM to update with new post to connect to
      this.jsPlumb?.batch(() => {
        const newConnections: PostConnection[] = difference(newVal, oldVal) ?? []
        newConnections.forEach(this.addConnection)
      })
    })
  }

  connectionsWithDisconnectOverlays: TrackedConnection[] = []

  get existingConnectionsArray(): TrackedConnection[] {
    return Object.values(this.existingConnections).filter((c) => c != null) as TrackedConnection[]
  }

  @Watch('postToDisconnectFromId')
  handlePostToDisconnectFromIdChanged(postToDisconnectFromId: Id | null): void {
    if (postToDisconnectFromId == null) {
      this.connectionsWithDisconnectOverlays.forEach((connection) => {
        this.jsPlumb?.removeOverlay(connection.jsplumbConnection, 'disconnect')
      })
      this.connectionsWithDisconnectOverlays = []
      return
    }
    const connectionsToUpdate = this.existingConnectionsArray.filter((connection) => {
      return (
        connection?.postConnection.from_wish_id === postToDisconnectFromId ||
        connection?.postConnection.to_wish_id === postToDisconnectFromId
      )
    })
    this.connectionsWithDisconnectOverlays = this.connectionsWithDisconnectOverlays.concat(connectionsToUpdate)
    connectionsToUpdate.forEach(this.addDisconnectOverlay)
  }

  drawAllConnections(): void {
    this.jsPlumb?.batch(() => {
      this.currentConnections.forEach((connection: PostConnection) => {
        this.addConnection(connection)
      })
    })
  }

  removeAllConnections(): void {
    this.jsPlumb?.deleteEveryConnection()
  }

  addConnection(postConnection: PostConnection): void {
    if (this.existingConnections[postConnection.id] != null) return // skip if already exists
    const jsplumbConnection = this.drawConnection(
      `${postConnection.from_wish_id}`,
      `${postConnection.to_wish_id}`,
      postConnection.label,
    )
    if (jsplumbConnection == null) return
    this.existingConnections[postConnection.id] = { postConnection, jsplumbConnection }
  }

  removeConnection(postConnection: PostConnection): void {
    const jsplumbConnection = this.existingConnections[postConnection.id]?.jsplumbConnection
    if (jsplumbConnection == null) return
    if (this.jsPlumb == null) return
    if (this.jsPlumb.deleteConnection(jsplumbConnection)) {
      this.existingConnections[postConnection.id] = undefined // remove from existing connections
    }
  }

  addDisconnectOverlay(connection: TrackedConnection): void {
    const { postConnection, jsplumbConnection } = connection
    const disconnectOverlay = (_component: HTMLElement): HTMLDivElement => {
      const div = document.createElement('div')
      div.innerHTML = `<button class="floating-action-button-connector wish-disconnect-button"><i class="immaterial-icons">close</i></button>`
      div.addEventListener('click', () => {
        this.completeDisconnection({ connectionId: postConnection.id })
      })
      return div
    }
    this.jsPlumb?.addOverlay(jsplumbConnection, {
      type: 'Custom',
      options: { create: disconnectOverlay, location: 0.3, id: 'disconnect' },
    })
  }

  drawConnection(fromId: string, toId: string, label: string): JsPlumbConnection<HTMLElement> | undefined {
    if (this.jsPlumb == null || this.BezierConnector == null) return
    let overlays: OverlaySpec[] = []
    const fromElement = document.getElementById('wish-' + fromId)
    const toElement = document.getElementById('wish-' + toId)

    if (fromElement == null || toElement == null) return undefined
    overlays = [
      {
        type: 'Arrow',
        options: {
          location: 1,
          width: 12,
          length: 12,
        },
      },
    ]
    if (label != null && label.length > 0) {
      overlays.push({
        type: 'Label',
        options: {
          label,
          id: 'label',
          cssClass: 'wish-connection-label',
        },
      })
    }
    const jsPlumb = this.jsPlumb
    return jsPlumb.connect({
      source: fromElement,
      target: toElement,
      endpoint: 'Blank',
      connector: {
        type: this.BezierConnector?.type ?? 'Straight',
        options: {
          curviness: 50,
        },
      },
      anchor: 'AutoDefault',
      overlays,
    })
  }
}
